import {Component, OnInit, inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {DataService} from "../../../services/data.service";
import {AccessService} from "../../../services/access.service";
import {ViewMode} from "../../interfaces";
import {Observable, Observer, of, switchMap} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {map} from "rxjs/operators";
import {TypeaheadMatch, TypeaheadModule} from "ngx-bootstrap/typeahead";
import {NgIf, NgFor, DatePipe} from '@angular/common';
import {YesNoPipe} from '../../../pipes/yes-no.pipe';
import {TeamRolePipe} from '../../../pipes/team-role.pipe';

interface UserForm {
    id: FormControl<string | null>;
    azure_id: FormControl<string | null>;
    name: FormControl<string>;
    short: FormControl<string>;
    email: FormControl<string>;
    last_login: FormControl<string | null>;
    active: FormControl<boolean>;
    roles: FormControl<string[]>;
    _userSearch: FormControl<string>;
}

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styleUrl: './user-detail.component.scss',
    imports: [NgIf, FormsModule, ReactiveFormsModule, TypeaheadModule, NgFor, DatePipe, YesNoPipe, TeamRolePipe]
})
export class UserDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private toastrService = inject(ToastrService);

    readonly ViewMode = ViewMode;

    form!: FormGroup<UserForm>;
    id_user!: string;
    viewMode!: ViewMode;
    user: any;
    rights: any[];

    users$!: Observable<any[]>;
    noUserResult = false;
    selectedUser: any;

    loading = false;

    ngOnInit(): void {
        this.id_user = this.route.snapshot.paramMap.get('id');
        this.viewMode = this.id_user == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: this.fb.control(null),
            azure_id: this.fb.control(null),
            name: this.fb.control(''),
            short: this.fb.control(null),
            email: this.fb.control(''),
            roles: this.fb.control([]),
            last_login: this.fb.control(null),
            active: this.fb.control(true),
            _userSearch: this.fb.control(null),
        });

        if (this.id_user !== '0') {
            this.getUser();
        } else {
            this.dataService.request('Right/listAll').subscribe(response => {
                this.rights = response.rights;

                this.rights.forEach(right => {
                    right.granted = false;
                })
            });
        }

        // search users
        this.users$ = new Observable((observer: Observer<string | undefined>) => {
            observer.next(this.form.get('_userSearch').value);
        }).pipe(
            switchMap((query: string) => {
                if (query) {
                    this.noUserResult = false;

                    return this.dataService.request('User/listAzure', {
                        search: query
                    }).pipe(
                        map((data: any) => data.users)
                    );
                }

                return of([]);
            })
        );
    }

    onSelectUser($event: TypeaheadMatch): void {
        this.selectedUser = $event.item;
    }

    typeaheadUsersNoResults($event: boolean): void {
        this.noUserResult = $event;
    }

    typeaheadUsersLoading($event: boolean): void {
        this.loading = $event;
    }

    getUser(): void {
        this.dataService.request('User/get', {
            id_user: this.id_user
        }).subscribe(response => {
            this.user = response.user;

            this.dataService.request('Right/listAll').subscribe(response => {
                this.rights = response.rights;

                this.rights.forEach(right => {
                    right.granted = this.user.rights.includes(right.code);
                })
            });

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.user.id,
            azure_id: this.user.azure_id,
            name: this.user.name,
            short: this.user.short,
            email: this.user.email,
            last_login: this.user.last_login,
            active: this.user.active == '1',
            roles: this.user.roles ? this.user.roles.split(',') : [],
        });
    }

    save(): void {
        this.form.patchValue({
            id: this.selectedUser.id,
            name: this.selectedUser.displayName,
            email: this.selectedUser.mail,
        })

        this.dataService.request('User/save', {
            userData: this.form.getRawValue(),
            new_rights: this.rights.filter(right => right.granted).map(right => right.id),
        }).subscribe(response => {
            if (response.isNew) {
                this.toastrService.success('Benutzer angelegt');
                this.router.navigate(['/user']);
            } else {
                this.toastrService.error('Dieser Benutzer existiert bereits');
            }
        });
    }

    update(): void {
        this.dataService.request('User/update', {
            userData: this.form.getRawValue(),
            new_rights: this.rights.filter(right => right.granted).map(right => right.id),
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getUser();
        });
    }

    cancel(): void {
        this.router.navigate(['/user']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    toggleRights(granted) {
        this.rights.forEach(right => right.granted = granted);
    }
}
